import { useMutation, useQuery } from "@apollo/client/react";
import {
  Box,
  Card,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import { CalendarIcon } from "@phosphor-icons/react/dist/csr/Calendar";
import { CheckCircleIcon } from "@phosphor-icons/react/dist/csr/CheckCircle";
import { ClockIcon } from "@phosphor-icons/react/dist/csr/Clock";
import { FileTextIcon } from "@phosphor-icons/react/dist/csr/FileText";
import { PencilIcon } from "@phosphor-icons/react/dist/csr/Pencil";
import { UserCircleIcon } from "@phosphor-icons/react/dist/csr/UserCircle";
import { XCircleIcon } from "@phosphor-icons/react/dist/csr/XCircle";
import { format } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Permissions from "../../constants/permissions";
import { BACKFILL_DETAIL } from "../../graphql";
import { SAVE_BACKFILL_MUTATION } from "../../graphql/mutations/backfill";
import { BACKFILL_MESSAGES } from "../../graphql/queries/backfills";
import type { BackfillDetailQuery, BackfillDetailQueryVariables } from "../../graphql/types";
import { Backfill, Event } from "../../graphql/types";
import { toLocalAndUtcDates } from "../../lib/Util";
import { StaticDiagram } from "../BackfillModal/Diagram";
import { DataLoader, EmptyContainer, LoadingContainer, SuccessContainer } from "../DataLoader";
import { EventLogTable } from "../EventLogTable";
import { InfiniteScrollContainer } from "../InfiniteScrollContainer";
import { IfPermission } from "../Permissions";
import { GqlError } from "../common/GqlError";
import { CountDisplay } from "../ui/CountDisplay";
import { EmptyContainer as Empty } from "../ui/EmptyContainer";
import { IconCopyToClipboard } from "../ui/IconCopyToClipboard";
import { KbdShortcut } from "../ui/Kbd";
import { LoadingIcon } from "../ui/LoadingIcon";
import { UserAvatarWithTooltip } from "../ui/UserAvatar";
import { Badge } from "../ui/badges";
import { StatefulButton } from "../ui/muiButtons";
import { SkeletonBackfillRuns, SkeletonRow } from "../ui/skeletons";
import BackfillStatus from "./BackfillStatus";
import { RunsTab } from "./RunsTab";

const MAX_SHORT_DESCRIPTION_LENGTH = 400;

const DateRangeIcon = styled(CalendarIcon)({
  width: "25px",
  height: "25px",
  flexShrink: 0,
});

const AccessTimeIconStyled = styled(ClockIcon)({
  width: "25px",
  height: "25px",
  flexShrink: 0,
});

const DescriptionIconStyled = styled(FileTextIcon)({
  width: "25px",
  height: "25px",
  flexShrink: 0,
});
const AccountCircleIconStyled = styled(UserCircleIcon)({
  width: "25px",
  height: "25px",
  flexShrink: 0,
});

const BackfillDetailContainer = styled(Card)(({ theme }) => ({
  marginTop: "10px",
  padding: theme.spacing(2),
}));

const ConnectionCard = styled(Paper)(({ theme }) => ({
  height: "45vh",
  overflowY: "auto",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginTop: theme.spacing(2),
}));

const getGraphLinksAndDownloads = (backfill: Backfill) => {
  const links: { [index: string]: string[] } = {};
  /* Download names: If it is a simple script, then we just use the download_names of the backfill to link with the uploads.
  if it is a bundle, then we wanna link the name of the bundle with the uploads
  */
  let download_names: Backfill["download_names"] = backfill.download_names;
  backfill.subgraph.download_declarations.forEach((elem) => {
    let name = "";
    if (!elem) {
      return;
    }
    if (elem.bundle_name) {
      name = elem.bundle_name;
    } else {
      name = elem.tables[0]?.name ?? "";
    }
    const newExports: string[] = [];
    if (elem.bundle_name) {
      //if it is a bundle, i have to go through all of the tables
      elem.tables.forEach((tableElem) => {
        tableElem?.exports.forEach((exp) => {
          // If the export is included is present in the uploads array, it means that it was selected in the backfill, so we probably want to show it
          // Also, we need to check that the download is included in the download_names array, if thats the case we link the download with the upload.
          if (exp && backfill.upload_names.includes(exp.name) && backfill.download_names.includes(tableElem.name)) {
            newExports.push(exp.name);
          }
        });
        /*
         iterate through download_names and delete the downloads, because it is a bundle and we wanna link the
        name of the bundle with the uploads
        */
        download_names = download_names.filter((item) => item == tableElem?.name);
      });
      // Now that we deleted the downloads, we can add the bundle name to the download_names array
      download_names.push(elem.bundle_name);
      links[`${name}`] = newExports;
    } else {
      // If it is not a bundle, we only have one table
      elem.tables[0]?.exports.forEach((element) => {
        if (element && backfill.upload_names.includes(element.name) && backfill.download_names.includes(name)) {
          newExports.push(element.name);
        }
      });
      links[`${name}`] = newExports;
    }
  });
  return { links, download_names };
};

type IBackfillInfo = {
  title: string;
  description: string;
};

const MessagesTab = ({ backfillId }: { backfillId: string }) => {
  const PAGE_SIZE = 50;
  const variables = {
    first: PAGE_SIZE,
    backfill_id: backfillId,
  };
  return (
    <InfiniteScrollContainer<Event>
      first={PAGE_SIZE}
      className={"eventlog-browser"}
      query={BACKFILL_MESSAGES}
      variables={variables}
      nodeName="events"
      data-cy="events-infinite-scroll-container"
    >
      {({ loading, error, results, hasResults, isLoadingMore }) => {
        const rowCount = results ? results.length : 0;
        return (
          <DataLoader
            loading={loading && !isLoadingMore}
            error={error}
            success={hasResults}
            defaults={{ small: false }}
          >
            <LoadingContainer>
              <SkeletonBackfillRuns rows={PAGE_SIZE} />
            </LoadingContainer>
            <EmptyContainer>
              <Empty title="No Messages" />
            </EmptyContainer>
            <SuccessContainer>
              <EventLogTable events={results as Event[]} />
              {isLoadingMore && <SkeletonRow />}
              <CountDisplay count={rowCount} testLabel="event-count-top" entity="event" sticky={false} />
            </SuccessContainer>
          </DataLoader>
        );
      }}
    </InfiniteScrollContainer>
  );
};

const Label = styled(Typography)(({ theme }) => ({
  fontSize: "12px",
  color: theme.palette.text50.main,
  cursor: "default",
  minWidth: "100px",
}));

const CancelledStatus = () => {
  return <Badge size="small" severity="error" label="Cancelled" data-cy="cancelled-status" />;
};

const CompletedStatusChip = (
  <Badge icon={<CheckCircleIcon />} size="small" severity="success" label="Completed" data-cy="chip-completed" />
);

const TabsManager: React.FC<{ backfill: Backfill }> = ({ backfill }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { links, download_names } = getGraphLinksAndDownloads(backfill);
  const currentTab = searchParams.get("currentTab");
  useEffect(() => {
    if (!currentTab) {
      const currentParams = Object.fromEntries([...searchParams]);
      setSearchParams({ ...currentParams, currentTab: "Connections" }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack>
      <Tabs
        value={currentTab || false}
        onChange={(event, newValue) => {
          const currentParams = Object.fromEntries([...searchParams]);
          setSearchParams({ ...currentParams, currentTab: newValue });
        }}
        centered
      >
        <Tab value="Connections" label="Connections" test-label="connections-tab" />
        <Tab value="Runs" label="Runs" test-label="runs-tab" />
        <Tab value="Messages" label="Messages" test-label="messages-tab" />
      </Tabs>
      {currentTab === "Connections" && (
        <ConnectionCard>
          <Stack sx={{ width: "100%", height: "100%" }}>
            <StaticDiagram
              currentSubgraph={{
                import_names: download_names,
                export_names: backfill.upload_names,
                links: links,
              }}
              showIcons
            />
          </Stack>
        </ConnectionCard>
      )}

      {currentTab === "Runs" && <RunsTab backfillId={backfill.id} />}
      {currentTab === "Messages" && <MessagesTab backfillId={backfill.id} />}
    </Stack>
  );
};

const BackfillCard = ({ backfill }: { backfill: Backfill }) => {
  const hasLongDescription = backfill.description.length > MAX_SHORT_DESCRIPTION_LENGTH;
  const [editing, setEditing] = useState<IEditing>({
    title: false,
    description: false,
  });
  const titleRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLInputElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (titleRef.current && editing.title) titleRef.current.focus();
    if (descriptionRef.current && editing.description) descriptionRef.current.focus();
  }, [titleRef, descriptionRef, editing.title, editing.description]);

  const [cancelBackfill, cancelState] = useMutation(SAVE_BACKFILL_MUTATION, {
    awaitRefetchQueries: false,
    refetchQueries: () => ["backfill_detail"],
    onCompleted: () => {
      toast.success("Backfill cancelled");
    },
    onError: () => {
      toast.error("Error cancelling backfill");
    },
  });
  const { control, getValues, setError, resetField } = useForm<IBackfillInfo>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: { description: backfill.description, title: backfill.title },
  });
  const [saveBackfill] = useMutation(SAVE_BACKFILL_MUTATION, {
    awaitRefetchQueries: false,
    refetchQueries: () => ["backfill_detail"],
    onCompleted: () => {
      toast.success("Backfill info edited successfully");
      if (editing.title && searchParams.get("backfillName") !== getValues("title")) {
        const currentParams = Object.fromEntries([...searchParams]);
        setSearchParams({ ...currentParams, backfillName: getValues("title") });
      }
      setEditing({ title: false, description: false });
    },
    onError: () => {
      toast.error("Failed to edit backfill info");
    },
  });
  const handleKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>, keyAttribute: string) => {
    const newTitle = getValues("title");
    const newDescription = getValues("description");
    switch (e.key) {
      case "Enter":
        e.preventDefault();
        if (keyAttribute === "title") {
          if (newTitle !== backfill.title) {
            if (!newTitle) {
              setError("title", { message: "Backfill must have a name" });
              return;
            }
            saveBackfill({
              variables: {
                backfill_id: backfill.id,
                title: newTitle,
              },
              optimisticResponse: {
                __typename: "Mutation",
                save_backfill: {
                  __typename: "Backfill",
                  id: backfill.id,
                  title: backfill.title,
                  status: backfill.status,
                  description: backfill.description,
                },
              },
            });
          } else {
            setEditing({ ...editing, title: false });
          }
        } else {
          if (backfill.description !== newDescription) {
            saveBackfill({
              variables: {
                backfill_id: backfill.id,
                description: newDescription,
              },
              optimisticResponse: {
                __typename: "Mutation",
                save_backfill: {
                  __typename: "Backfill",
                  id: backfill.id,
                  title: backfill.title,
                  status: backfill.status,
                  description: backfill.description,
                },
              },
            });
          } else {
            setEditing({ ...editing, description: false });
          }
        }
        break;
      case "Escape":
        if (editing.title) {
          resetField("title");
          setEditing({ ...editing, title: false });
        } else {
          resetField("description");
          setEditing({ ...editing, description: false });
        }
        break;
      default:
        break;
    }
  };

  const parseDescription = (description: string) => {
    const longDescription = description || "";
    const suffix = longDescription.length > MAX_SHORT_DESCRIPTION_LENGTH ? " ..." : "";
    const shortDescription = `${longDescription.substring(0, MAX_SHORT_DESCRIPTION_LENGTH)}${suffix}`;
    return { longDescription, shortDescription };
  };

  const startDatetime = toLocalAndUtcDates(backfill.start_datetime);
  const endDatetime = toLocalAndUtcDates(backfill.end_datetime);

  const { longDescription, shortDescription } = parseDescription(backfill.description);

  return (
    <Stack spacing={1}>
      <BackfillDetailContainer data-cy="box-detail" variant="outlined">
        <Stack direction="row" spacing={2}>
          <Stack sx={{ flex: 1, pb: 2 }} spacing={2}>
            <Stack direction="row" sx={{ alignItems: "center", flexWrap: "wrap" }} spacing={1}>
              {editing.title ? (
                <Controller
                  name="title"
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => (
                    <>
                      <TextField
                        {...field}
                        variant="standard"
                        name="title"
                        data-cy="backfill-title"
                        autoComplete="off"
                        style={{
                          width: "50%",
                          marginBottom: "10px",
                        }}
                        onKeyDown={(e) => handleKeyDown(e, "title")}
                        disabled={!editing.title}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error && fieldState.error.message}
                        inputRef={titleRef}
                        onFocus={(e) => e.currentTarget.select()}
                      />
                      <Typography sx={{ ml: 1.2 }}>
                        Press <KbdShortcut>esc</KbdShortcut> to cancel; <KbdShortcut>enter</KbdShortcut> to save
                      </Typography>
                    </>
                  )}
                />
              ) : (
                <>
                  <Typography sx={{ fontSize: "25px", fontWeight: "bold" }} data-cy="backfill-title">
                    {backfill.title}
                  </Typography>
                  <IfPermission permission={Permissions.Backfill}>
                    <IconButton
                      data-cy="edit-field-title"
                      onClick={() => {
                        setEditing({ title: true, description: false });
                      }}
                    >
                      <PencilIcon />
                    </IconButton>
                  </IfPermission>
                </>
              )}
              <IconCopyToClipboard id={backfill.id} type="Backfill ID" />
            </Stack>
            <Stack spacing={3}>
              <Stack direction="row" sx={{ alignItems: "center" }} spacing={1}>
                <DateRangeIcon />
                <Label>Report date</Label>
                <Typography sx={{ fontSize: 14 }} data-cy="backfill-date-range">
                  {`${format(new Date(startDatetime.date), "MMM dd yy")} - ${format(
                    new Date(endDatetime.date),
                    "MMM dd yy"
                  )} UTC`}
                </Typography>
              </Stack>

              <Grid container spacing={2}>
                <Grid size={{ lg: 6, xs: 12 }}>
                  <Stack spacing={3}>
                    <Stack direction="row" sx={{ alignItems: "center" }} spacing={1}>
                      <AccountCircleIconStyled />
                      <Label>Created by</Label>
                      <UserAvatarWithTooltip
                        user={backfill.created_by}
                        prefix="Created by "
                        sxIconButton={{
                          ml: 0,
                          mr: 0,
                          p: 0,
                        }}
                      />
                      <Typography sx={{ fontSize: 14, textOverflow: "ellipsis", overflow: "hidden" }}>
                        {backfill.created_by.name}
                      </Typography>
                    </Stack>
                    <Stack direction="row" sx={{ alignItems: "center" }} spacing={1}>
                      <AccessTimeIconStyled />
                      <Label>Created on</Label>
                      <Typography sx={{ fontSize: 14 }} data-cy="backfill-created-day">
                        {toLocalAndUtcDates(backfill.created_on).utc}
                      </Typography>
                    </Stack>
                  </Stack>
                </Grid>
                {backfill.status === "cancelled" && backfill.cancelled_by && (
                  <Grid size={{ lg: 6, xs: 12 }}>
                    <Stack spacing={3}>
                      <Stack direction="row" sx={{ alignItems: "center" }} spacing={1}>
                        <AccountCircleIconStyled />
                        <Label>Cancelled by</Label>
                        <UserAvatarWithTooltip
                          user={backfill.cancelled_by}
                          prefix="Cancelled by "
                          sxIconButton={{
                            ml: 0,
                            mr: 0,
                            p: 0,
                          }}
                        />
                        <Typography sx={{ fontSize: 14, overflow: "hidden" }} data-cy="backfill-canceled-name-user">
                          {backfill.cancelled_by?.name}
                        </Typography>
                      </Stack>
                      {backfill.cancelled_on && (
                        <Stack direction="row" sx={{ alignItems: "center" }} spacing={1}>
                          <AccessTimeIconStyled />
                          <Label>Cancelled on</Label>
                          <Typography sx={{ fontSize: 14 }} data-cy="backfill-canceled-day">
                            {toLocalAndUtcDates(backfill.cancelled_on).utc}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  </Grid>
                )}
              </Grid>

              <Stack direction="row" sx={{ alignItems: "center" }} spacing={1}>
                <Stack direction="row" sx={{ alignItems: "center", whiteSpace: "nowrap" }} spacing={1}>
                  <DescriptionIconStyled />
                  <Label>Description</Label>
                </Stack>
                <Stack direction="row" sx={{ alignItems: "center", flexWrap: "wrap", flex: 1 }}>
                  {editing.description ? (
                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <>
                          <TextField
                            {...field}
                            name="description"
                            data-cy="backfill-description"
                            multiline
                            minRows={4}
                            maxRows={5}
                            autoComplete="off"
                            onKeyDown={(e) => handleKeyDown(e, "description")}
                            inputRef={descriptionRef}
                            style={{
                              width: "100%",
                            }}
                            onFocus={(e) =>
                              // Move cursor to the end of the description.
                              e.currentTarget.setSelectionRange(
                                e.currentTarget.value.length,
                                e.currentTarget.value.length
                              )
                            }
                            slotProps={{
                              input: {
                                endAdornment: (
                                  <InputAdornment
                                    position="end"
                                    sx={{
                                      position: "absolute",
                                      right: "5px",
                                      bottom: "10px",
                                    }}
                                  >
                                    / {4000 - getValues("description").length}
                                  </InputAdornment>
                                ),
                              },

                              htmlInput: {
                                maxLength: 4000,
                              },
                            }}
                            disabled={!editing.description}
                          />
                          <Typography sx={{ ml: 1 }}>
                            Press <KbdShortcut>esc</KbdShortcut> to cancel; <KbdShortcut>enter</KbdShortcut> to save
                          </Typography>
                        </>
                      )}
                    />
                  ) : (
                    <>
                      <Tooltip
                        title={backfill.description}
                        slotProps={{
                          popper: { sx: { maxWidth: "50%" } },
                        }}
                      >
                        <Typography
                          sx={{ fontSize: 14, alignItems: "center", maxWidth: "85%" }}
                          data-cy="backfill-description"
                        >
                          {
                            //prettier-ignore
                            !backfill.description ? "-" :
                          hasLongDescription ? shortDescription :
                              longDescription
                          }
                        </Typography>
                      </Tooltip>
                      <IfPermission permission={Permissions.Backfill}>
                        <IconButton
                          data-cy="edit-field-description"
                          sx={{ alignSelf: "flex-start" }}
                          onClick={() => {
                            setEditing({ title: false, description: true });
                          }}
                        >
                          <PencilIcon />
                        </IconButton>
                      </IfPermission>
                    </>
                  )}
                </Stack>
              </Stack>
            </Stack>
          </Stack>
          {(() => {
            const isInProgress = backfill.status === "running" || backfill.status === "preparing";
            return (
              <Stack
                direction="column"
                sx={{ alignItems: "flex-end", flexShrink: 0, alignSelf: isInProgress ? "stretch" : "flex-start" }}
              >
                {isInProgress && (
                  <IfPermission permission={Permissions.Backfill}>
                    <StatefulButton
                      variant="outlined"
                      color="error"
                      loading={cancelState.loading}
                      startIcon={<XCircleIcon />}
                      onClick={() =>
                        cancelBackfill({
                          variables: {
                            backfill_id: backfill.id,
                            status: "cancelled",
                          },
                        })
                      }
                      data-cy="cancel-backfill-button"
                    >
                      Cancel
                    </StatefulButton>
                  </IfPermission>
                )}
                <Box sx={isInProgress ? { flex: 1, display: "flex", alignItems: "center", minWidth: 300 } : {}}>
                  <Box sx={{ width: "100%" }}>
                    <BackfillStatus
                      status={backfill.status}
                      totalPipelines={backfill.total_pipelines}
                      succeededPipelines={backfill.succeeded_pipelines}
                      failedPipelines={backfill.failed_pipelines}
                      cancelledPipelines={backfill.cancelled_pipelines}
                      hasDirtyRows={backfill.hasDirtyRows || false}
                    />
                  </Box>
                </Box>
              </Stack>
            );
          })()}
        </Stack>
      </BackfillDetailContainer>
      <TabsManager backfill={backfill} />
    </Stack>
  );
};

type IEditing = {
  title: boolean;
  description: boolean;
};

const BackfillDetails = () => {
  const { backfillId } = useParams();
  const { loading, error, data } = useQuery<BackfillDetailQuery, BackfillDetailQueryVariables>(BACKFILL_DETAIL, {
    variables: {
      backfill_id: backfillId ?? "",
    },
    errorPolicy: "all",
    fetchPolicy: "cache-and-network",
  });

  if (loading) return <LoadingIcon />;
  if (error) return <GqlError error={error} />;
  if (data?.backfill) {
    return <BackfillCard backfill={data.backfill as Backfill} />;
  }
  return null;
};

export { BackfillDetails, CancelledStatus, CompletedStatusChip };
